import { makeFloorRails } from "building-generation/elements/floor/floorSystem";
import getTargetedPorchSettings from "building-generation/elements/porch/getTargetedPorchSettings";
import {
  isWallFramingAStud,
  findStudLumberLength,
  findHeaderLumberLength,
  isWallFramingAHeader,
  isWallFramingAPlate,
  findPlateLumberLength,
  isWallFramingABlocking,
  groupFramingByProfileDescriptor,
  updateLumberCountersByProfileDescriptor,
} from "building-generation/elements/walls/wallFramingUtils";
import type { Building } from "building-types/building/buildingTypes";
import type { BuildingEntityFilter } from "building-types/dataSequences/filterableBuildingEntityTypes";
import type { FloorSystem } from "building-types/floors/floorSystemTypes";
import type { StandardLumberCounterSet } from "building-types/framing/lumberTypes";
import { WallFramingType, type WallFraming } from "building-types/walls/wallFramingTypes";
import type { RWTable, Table } from "common/data/table";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";

interface BuildingFraming {
  readonly wallFramingSystems: Table<WallFraming[]>; // keyed by levelId
  readonly gableEndWallFramingSystems: Table<WallFraming[]>; // keyed by levelId
  readonly floorFramingSystems: Table<FloorSystem>; // keyed by levelId
  readonly lumberCounters: StandardLumberCounterSet;
}

function updateLumberCountersForWallFraming(
  lumberCounters: StandardLumberCounterSet,
  wallFramings: WallFraming[],
  filter?: BuildingEntityFilter,
) {
  for (const wallFraming of wallFramings) {
    const filteredFramings = filter ? wallFraming.framings.filter(filter) : wallFraming.framings;

    const studFramings = filteredFramings.filter((framing) => isWallFramingAStud(framing));
    const plateFramings = filteredFramings.filter(
      (framing) => isWallFramingAPlate(framing) || isWallFramingABlocking(framing),
    );
    const headerFramings = filteredFramings.filter((framing) => isWallFramingAHeader(framing));

    const studFramingsGrouped = groupFramingByProfileDescriptor(studFramings);
    const plateFramingsGrouped = groupFramingByProfileDescriptor(plateFramings);
    const headerFramingsGrouped = groupFramingByProfileDescriptor(headerFramings);

    //Note: The type is not used at all. It is kept for backward compatibility.
    //The ideal use of the type would be to group the framings by type. Not sure if it makes sense also.
    for (const framing of studFramingsGrouped) {
      updateLumberCountersByProfileDescriptor(
        lumberCounters,
        framing.profileDescriptor,
        framing.lumberRails,
        WallFramingType.STANDARD_STUD,
        true,
        findStudLumberLength,
      );
    }
    for (const framing of plateFramingsGrouped) {
      updateLumberCountersByProfileDescriptor(
        lumberCounters,
        framing.profileDescriptor,
        framing.lumberRails,
        WallFramingType.BOTTOM_SILL_PLATE,
        false,
        findPlateLumberLength,
      );
    }
    for (const framing of headerFramingsGrouped) {
      updateLumberCountersByProfileDescriptor(
        lumberCounters,
        framing.profileDescriptor,
        framing.lumberRails,
        WallFramingType.HEADER,
        true,
        findHeaderLumberLength,
      );
    }
  }
}

export function makeBuildingFraming(building: Building, filter?: BuildingEntityFilter): BuildingFraming {
  const lumberCounters: StandardLumberCounterSet = {};

  const wallFramingSystems = building.wallFramingSystems;
  for (const levelId in wallFramingSystems) {
    updateLumberCountersForWallFraming(lumberCounters, wallFramingSystems[levelId], filter);
  }

  const gableEndWallFramingSystems = building.gableEndWallFramingSystems;
  for (const levelId in gableEndWallFramingSystems) {
    updateLumberCountersForWallFraming(lumberCounters, gableEndWallFramingSystems[levelId], filter);
  }
  const floorFramingSystems = makeBuildingFloorFraming(building, lumberCounters, filter);

  return { wallFramingSystems, gableEndWallFramingSystems, floorFramingSystems, lumberCounters };
}

export function makeBuildingFloorFraming(
  {
    levels,
    walls,
    planarGraphs,
    planarGraphLevelAssociations,
    rooms,
    roomsByLevel,
    roomLevelIndices,
    roomFloorAssemblies,
    roomClassFloorAssemblyGroups,
    settings,
    interiorPalette,
    exteriorPalette,
    floorPaths,
    settingsSets,
    optionChoices,
    doc: {
      settings: docSettings,
      entitySettings,
      frameFloorAssemblies,
      framingComponents,
      slabComponents,
      sheathingComponents,
    },
  }: Building,
  lumberCounters: StandardLumberCounterSet = {},
  filter?: BuildingEntityFilter,
): Table<FloorSystem> {
  const floorFramingSystems: RWTable<FloorSystem> = {};
  const targetedPorchSettings = getTargetedPorchSettings(
    { settingsSets, optionChoices, settings, rooms },
    { settings: docSettings, entitySettings },
  );

  for (const levelId in levels) {
    const levelRooms = roomsOnLevel(levelId, rooms, roomsByLevel);
    const filteredRooms = filter ? levelRooms.filter(filter) : levelRooms;

    floorFramingSystems[levelId] = makeFloorRails({
      levelId,
      levels,
      planarGraph: planarGraphs[levelId],
      planarGraphLevelAssociations: planarGraphLevelAssociations[levelId],
      frameFloorAssemblies,
      framingComponents,
      slabComponents,
      sheathingComponents,
      rooms: filteredRooms,
      roomLevelIndices,
      roomFloorAssemblies,
      roomClassFloorAssemblyGroups,
      wallSystem: walls[levelId],
      interiorPalette,
      exteriorPalette,
      floorPaths: floorPaths[levelId],
      settings: {
        wallSettings: settings.wall,
        porchSettings: settings.porch,
        rearPorchSettings: settings.rearPorch,
        columnSettings: settings.column,
        foundationSettings: settings.foundation,
      },
      lumberCounters,
      targetedPorchSettings,
    });
  }

  return floorFramingSystems;
}
